<script>
  import { createEventDispatcher, onMount } from 'svelte';
  import { writable, get } from 'svelte/store';
  import { selectedStudy } from '$stores/selectionStore';
  import { user, userRoles } from '$stores/sessionStore';
  import { fetchStudyProperties as refreshStudyProperties } from '$stores/configStore';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import {
    faSquarePlus,
    faFolderPlus,
    faTrashCan,
    faInfoCircle,
    faRotate,
    faGlobe,
    faIdCard,
    faCog,
    faChartBar,
    faDroplet,
    faList,
    faEllipsisVertical,
    faFileExport,
    faFileImport,
    faLock,
  } from '@fortawesome/pro-light-svg-icons';
  import {
    ACCESS_UI_GROUPS,
    ACCESS_ROLE_OPTIONS,
    defaultRolesForAccessAction,
    actionsForAccessGroup,
  } from '$lib/permissions/accessUiCatalog';

  // Using proxy endpoints instead of direct API access
  const API_BASE_URL = '/api/proxy';

  const dispatch = createEventDispatcher();
  export let showForm = false;
  // export let editProperties = null;

  const PROPERTY_TYPES = [
    { value: 'text', label: 'Text' },
    { value: 'number', label: 'Number' },
    { value: 'boolean', label: 'Boolean' },
    { value: 'date', label: 'Date' },
    { value: 'datetime', label: 'Date/Time' },
    { value: 'select', label: 'Select List' },
  ];

  // Tab configuration
  const TABS = [
    { id: 'languages', label: 'Languages', icon: faGlobe },
    { id: 'idMasks', label: 'Patterns', icon: faIdCard },
    { id: 'features', label: 'Features', icon: faCog },
    { id: 'access', label: 'Access', icon: faLock },
    { id: 'reports', label: 'Reports', icon: faChartBar },
    { id: 'glucose', label: 'Glucose', icon: faDroplet },
    { id: 'properties', label: 'Properties', icon: faList },
  ];

  let propertiesData = writable({
    properties: [],
    completedStatusCodes: ['COMPD'],
    activeStatusCodes: ['ENRO'],
    excludedSiteIds: [],
    schedulerEnabled: false,
    idMasks: {
      siteId: '',
      participantId: '{siteId}{###}',
      autoGenerateParticipantId: true,
      showGenerateParticipantIdButton: true,
    },
    features: {
      messaging: true,
      siteMessaging: true,
      pinReset: false,
      pinResetMessageTypeCode: 'MSGPINRESET',
      diary: true,
      diaryEditing: false,
      eventTimeline: false,
      smbg: true,
      workflows: false,
      performanceMetrics: true,
      documentManagement: {
        study: true,
        site: true,
        participant: true,
        showStudyFilesOnSite: false,
        autoShowStudy: false,
        autoShowSite: false,
        autoShowParticipant: false,
      },
      cardViews: {
        sites: true,
        participants: true,
        participant: true,
        cgmStatisticsReport: true,
        measurementsReport: true,
        agpReport: true,
      },
      schedule: true,
      compliance: true,
      auditLog: true,
      sensorUploads: {
        enabled: false,
        readerType: 'dexcom',
      },
      siteCgmExport: false,
      randomization: {
        enabled: false,
        daysAfterEnrollment: 30,
      },
      visitTags: true,
    },
    reports: {
      generalReports: {
        cgmDetails: true,
        cgmReadings: true,
        smbgDetails: true,
        diaryEntryDetails: true,
        insulinDailyTotals: true,
        alertsMessageDetails: true,
        eventReport: false,
        subjectCgm: true,
        sensorCgm: true,
        cgmCompletion: true,
      },
      participantReports: {
        cgmStatistics: true,
        measurements: true,
        agpDailyProfiles: true,
      },
      customReports: {
        periodCompliance: true,
        periodStatistics: true,
        cgmSensorStatistics: true,
        cgmParticipantStatistics: true,
      },
      reportSettings: {
        diaryEntryDatePriority: 'formDate',
      },
    },
    permissions: {
      actionRoles: {},
    },
    glucoseParameters: {
      cgmCompliance: {
        complianceThreshold: 80,
        complianceRange: 30,
        complianceFocus: 7,
      },
      cgmValidRange: {
        low: 40,
        high: 400,
      },
      smbgCompliance: {
        complianceThreshold: 80,
        complianceRange: 30,
        complianceFocus: 7,
        targetReadingCount: 4,
      },
      thresholds: {
        veryLow: 54,
        low: 70,
        high: 180,
        veryHigh: 250,
      },
      events: {
        hypoThreshold: 54,
        hyperThreshold: 250,
        eventDuration: 3,
        smbgHypoThreshold: 75,
        smbgHyperThreshold: 200,
      },
    },
  });

  let propertyGroups = writable([]);
  let error = '';
  let previousShowForm = false;
  let languages = [];
  let studyLanguages = [];
  let isLoadingLanguages = false;
  let languagesError = '';
  let messageTypeOptions = [];

  // Tab state
  let activeTab = 'languages';
  
  // Menu dropdown state
  let showActionsMenu = false;
  let fileInputRef = null;
  
  // Completed status codes input (comma-separated string for UI)
  let completedStatusCodesInput = '';

  // Active status codes input (comma-separated string for UI)
  let activeStatusCodesInput = '';

  // Excluded site IDs input (comma-separated string for UI)
  let excludedSiteIdsInput = '';
  
  // Update propertiesData when input changes
  function updateCompletedStatusCodes() {
    const codes = completedStatusCodesInput
      .split(',')
      .map(code => code.trim().toUpperCase())
      .filter(code => code.length > 0);
    propertiesData.update(data => ({
      ...data,
      completedStatusCodes: codes.length > 0 ? codes : ['COMPD']
    }));
  }

  function updateActiveStatusCodes() {
    const codes = activeStatusCodesInput
      .split(',')
      .map(code => code.trim().toUpperCase())
      .filter(code => code.length > 0);
    propertiesData.update(data => ({
      ...data,
      activeStatusCodes: codes.length > 0 ? codes : ['ENRO']
    }));
  }

  function updateExcludedSiteIds() {
    const ids = excludedSiteIdsInput
      .split(',')
      .map(id => id.trim())
      .filter(id => id.length > 0);
    propertiesData.update(data => ({
      ...data,
      excludedSiteIds: ids
    }));
  }
  
  // Initialize completedStatusCodesInput from propertiesData (called after fetch)
  function initCompletedStatusCodesInput() {
    if ($propertiesData.completedStatusCodes && Array.isArray($propertiesData.completedStatusCodes)) {
      completedStatusCodesInput = $propertiesData.completedStatusCodes.join(', ');
    } else {
      completedStatusCodesInput = 'COMPD';
    }
  }

  function initActiveStatusCodesInput() {
    if ($propertiesData.activeStatusCodes && Array.isArray($propertiesData.activeStatusCodes)) {
      activeStatusCodesInput = $propertiesData.activeStatusCodes.join(', ');
    } else {
      activeStatusCodesInput = 'ENRO';
    }
  }

  function initExcludedSiteIdsInput() {
    if ($propertiesData.excludedSiteIds && Array.isArray($propertiesData.excludedSiteIds)) {
      excludedSiteIdsInput = $propertiesData.excludedSiteIds.join(', ');
    } else {
      excludedSiteIdsInput = '';
    }
  }

  function effectiveAccessActionRoles(actionKey, permissions) {
    const stored = permissions?.actionRoles?.[actionKey];
    return Array.isArray(stored) ? stored : defaultRolesForAccessAction(actionKey);
  }

  function accessRoleChecked(actionKey, role, permissions) {
    return effectiveAccessActionRoles(actionKey, permissions).some(
      (r) => String(r).toLowerCase() === String(role).toLowerCase()
    );
  }

  function toggleAccessActionRole(actionKey, role, checked) {
    propertiesData.update((data) => {
      const permissions = data.permissions || { actionRoles: {} };
      const actionRoles = { ...(permissions.actionRoles || {}) };
      const defaults = defaultRolesForAccessAction(actionKey);
      let current = Array.isArray(actionRoles[actionKey]) ? [...actionRoles[actionKey]] : [...defaults];
      const rl = String(role).toLowerCase();
      if (checked) {
        if (!current.some((r) => String(r).toLowerCase() === rl)) {
          current.push(role);
        }
      } else {
        current = current.filter((r) => String(r).toLowerCase() !== rl);
      }
      actionRoles[actionKey] = current;
      return {
        ...data,
        permissions: { ...permissions, actionRoles },
      };
    });
  }
  
  // Add computed property for admin access
  $: isAdmin = $userRoles && Array.isArray($userRoles) && $userRoles.some(role => role.toLowerCase() === 'admin');
  let expandedSubsections = new Set(['basicFeatures']);

  // Watch for changes to showForm and selectedStudy
  $: if (showForm && !previousShowForm && $selectedStudy?._id) {
    previousShowForm = showForm;
    Promise.all([fetchStudyProperties(), fetchLanguages(), fetchStudyLanguages(), fetchMessageTypeOptions()]);
  } else if (!showForm) {
    previousShowForm = false;
  }

  // Ensure randomization object always exists
  $: if ($propertiesData.features && !$propertiesData.features.randomization) {
    propertiesData.update(data => ({
      ...data,
      features: {
        ...data.features,
        randomization: {
          enabled: false,
          daysAfterEnrollment: 30,
        }
      }
    }));
  }

  // Ensure sensorUploads object always exists
  $: if ($propertiesData.features && !$propertiesData.features.sensorUploads) {
    propertiesData.update(data => ({
      ...data,
      features: {
        ...data.features,
        sensorUploads: {
          enabled: false,
          readerType: 'dexcom',
        }
      }
    }));
  }

  // Ensure reportSettings/customReports objects always exist
  $: if ($propertiesData.reports) {
    const needsReportSettings = !$propertiesData.reports.reportSettings;
    const needsCustomReports = !$propertiesData.reports.customReports;
    if (needsReportSettings || needsCustomReports) {
      propertiesData.update(data => ({
        ...data,
        reports: {
          ...data.reports,
          reportSettings: needsReportSettings
            ? { diaryEntryDatePriority: 'formDate' }
            : data.reports.reportSettings,
          customReports: needsCustomReports
            ? {
                periodCompliance: true,
                periodStatistics: true,
                cgmSensorStatistics: true,
                cgmParticipantStatistics: true,
              }
            : data.reports.customReports,
        }
      }));
    }
  }

  onMount(async () => {
    if (showForm && $selectedStudy?._id) {
      await Promise.all([fetchStudyProperties(), fetchLanguages(), fetchStudyLanguages(), fetchMessageTypeOptions()]);
    }
  });

  async function fetchMessageTypeOptions() {
    try {
      const res = await fetch(`${API_BASE_URL}/lookups/MSGTYPE`);
      if (!res.ok) throw new Error('Failed to fetch message types');
      const data = await res.json();
      messageTypeOptions = data.data || [];
    } catch (err) {
      console.error('Error fetching message types:', err);
      messageTypeOptions = [];
    }
  }

  function toggleSubsection(section) {
    if (expandedSubsections.has(section)) {
      expandedSubsections.delete(section);
    } else {
      expandedSubsections.add(section);
    }
    expandedSubsections = expandedSubsections; // trigger reactivity
  }

  function isSubsectionExpanded(section) {
    return expandedSubsections.has(section);
  }

  async function fetchLanguages() {
    try {
      isLoadingLanguages = true;
      languagesError = '';
      const res = await fetch(`${API_BASE_URL}/languages?pageSize=999`);
      if (!res.ok) throw new Error('Failed to fetch languages');
      const data = await res.json();
      languages = data.data || [];
    } catch (err) {
      console.error('Error fetching languages:', err);
      languagesError = 'Failed to load languages';
      languages = [];
    } finally {
      isLoadingLanguages = false;
    }
  }

  async function fetchStudyLanguages() {
    try {
      const res = await fetch(`${API_BASE_URL}/languages/study/${$selectedStudy._id}`);
      if (!res.ok) throw new Error('Failed to fetch study languages');
      const data = await res.json();
      studyLanguages = Array.isArray(data) ? data : [];
    } catch (err) {
      console.error('Error fetching study languages:', err);
      error = 'Failed to load study languages';
      studyLanguages = [];
    }
  }

  function isLanguageAssigned(languageId) {
    return studyLanguages.some((sl) => sl.languageGUID === languageId);
  }

  function isDefaultLanguage(languageId) {
    const studyLang = studyLanguages.find((sl) => sl.languageGUID === languageId);
    return studyLang?.languageDefault || false;
  }

  async function toggleLanguageAssignment(languageId, isCurrentlyAssigned) {
    try {
      const method = isCurrentlyAssigned ? 'DELETE' : 'POST';
      const res = await fetch(
        `${API_BASE_URL}/languages/study/${$selectedStudy._id}/${languageId}`,
        {
          method,
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Failed to ${isCurrentlyAssigned ? 'remove' : 'assign'} language`
        );
      }

      await fetchStudyLanguages();
    } catch (err) {
      console.error('Error toggling language assignment:', err);
      error = err.message || `Failed to ${isCurrentlyAssigned ? 'remove' : 'assign'} language`;
    }
  }

  async function setDefaultLanguage(languageId) {
    try {
      const res = await fetch(
        `${API_BASE_URL}/languages/study/${$selectedStudy._id}/default/${languageId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!res.ok) {
        throw new Error('Failed to set default language');
      }

      await fetchStudyLanguages();
    } catch (err) {
      console.error('Error setting default language:', err);
      error = 'Failed to set default language';
    }
  }

  async function fetchStudyProperties() {
    try {
      const response = await fetch(`${API_BASE_URL}/studyProperties/${$selectedStudy._id}`);
      const data = await response.json();

      if (data.data) {
        // If idMasks doesn't exist in the data, initialize it with defaults
        if (!data.data.idMasks) {
          data.data.idMasks = {
            siteId: '',
            participantId: '{siteId}{###}',
            autoGenerateParticipantId: true,
            showGenerateParticipantIdButton: true,
          };
        } else {
          if (data.data.idMasks.autoGenerateParticipantId === undefined) {
            data.data.idMasks.autoGenerateParticipantId = true;
          }
          if (data.data.idMasks.showGenerateParticipantIdButton === undefined) {
            data.data.idMasks.showGenerateParticipantIdButton = true;
          }
        }

        // If glucoseParameters doesn't exist in the data, initialize it with defaults
        if (!data.data.glucoseParameters) {
          data.data.glucoseParameters = {
            cgmCompliance: {
              complianceThreshold: 80,
              complianceRange: 30,
              complianceFocus: 7,
            },
            cgmValidRange: {
              low: 40,
              high: 400,
            },
            smbgCompliance: {
              complianceThreshold: 80,
              complianceRange: 30,
              complianceFocus: 7,
              targetReadingCount: 4,
            },
            thresholds: {
              veryLow: 54,
              low: 70,
              high: 180,
              veryHigh: 250,
            },
            events: {
              hypoThreshold: 54,
              hyperThreshold: 250,
              eventDuration: 3,
            },
          };
        } else if (!data.data.glucoseParameters.events) {
          // If glucoseParameters exists but events doesn't, initialize events
          data.data.glucoseParameters.events = {
            hypoThreshold: 54,
            hyperThreshold: 250,
            eventDuration: 3,
            smbgHypoThreshold: 75,
            smbgHyperThreshold: 200,
          };
        } else {
          // If events exists but SMBG thresholds don't, add defaults for them
          if (data.data.glucoseParameters.events.smbgHypoThreshold === undefined) {
            data.data.glucoseParameters.events.smbgHypoThreshold = 75;
          }
          if (data.data.glucoseParameters.events.smbgHyperThreshold === undefined) {
            data.data.glucoseParameters.events.smbgHyperThreshold = 200;
          }
        }

        // Ensure cgmValidRange exists
        if (!data.data.glucoseParameters.cgmValidRange) {
          data.data.glucoseParameters.cgmValidRange = { low: 40, high: 400 };
        }

        // If reports doesn't exist in the data, initialize it with defaults
        if (!data.data.reports) {
          data.data.reports = {
            generalReports: {
              cgmDetails: true,
              cgmReadings: true,
              smbgDetails: true,
              diaryEntryDetails: true,
              insulinDailyTotals: true,
              alertsMessageDetails: true,
              eventReport: false,
              subjectCgm: true,
              sensorCgm: true,
              cgmCompletion: true,
            },
            participantReports: {
              cgmStatistics: true,
              measurements: true,
              agpDailyProfiles: true,
            },
            customReports: {
              periodCompliance: true,
              periodStatistics: true,
              cgmSensorStatistics: true,
              cgmParticipantStatistics: true,
            },
            reportSettings: {
              diaryEntryDatePriority: 'formDate',
            },
          };
        } else if (!data.data.reports.reportSettings) {
          // If reports exists but reportSettings doesn't, initialize it
          data.data.reports.reportSettings = {
            diaryEntryDatePriority: 'formDate',
          };
        }
        if (data.data.reports.generalReports && data.data.reports.generalReports.eventReport === undefined) {
          data.data.reports.generalReports.eventReport = false;
        }
        if (data.data.reports.generalReports && data.data.reports.generalReports.cgmCompletion === undefined) {
          data.data.reports.generalReports.cgmCompletion = true;
        }
        if (!data.data.reports.customReports) {
          data.data.reports.customReports = {
            periodCompliance: true,
            periodStatistics: true,
            cgmSensorStatistics: true,
            cgmParticipantStatistics: true,
          };
        }

        // If features doesn't exist in the data, initialize it with defaults
        if (!data.data.features) {
          data.data.features = {
            messaging: true,
            diary: true,
            diaryEditing: false,
            eventTimeline: false,
            smbg: true,
            workflows: false,
            performanceMetrics: true,
            documentManagement: {
              study: true,
              site: true,
              participant: true,
              showStudyFilesOnSite: false,
              autoShowStudy: false,
              autoShowSite: false,
              autoShowParticipant: false,
            },
            cardViews: {
              sites: true,
              participants: true,
              participant: true,
              cgmStatisticsReport: true,
              measurementsReport: true,
              agpReport: true,
            },
            schedule: true,
            compliance: true,
            auditLog: true,
            siteMessaging: true,
            pinReset: false,
            pinResetMessageTypeCode: 'MSGPINRESET',
            messageMuting: false,
            sensorUploads: {
              enabled: false,
              readerType: 'dexcom',
            },
            siteCgmExport: false,
            randomization: {
              enabled: false,
              daysAfterEnrollment: 30,
            },
          };
        } else {
          // If features exists but randomization doesn't, initialize randomization
          if (!data.data.features.randomization) {
            data.data.features.randomization = {
              enabled: false,
              daysAfterEnrollment: 30,
            };
          }
          // Ensure workflows flag exists with a default of false when missing
          if (data.data.features.workflows === undefined) {
            data.data.features.workflows = false;
          }
          // Ensure pinReset flag exists with a default of false when missing
          if (data.data.features.pinReset === undefined) {
            data.data.features.pinReset = false;
          }
          if (!data.data.features.pinResetMessageTypeCode) {
            data.data.features.pinResetMessageTypeCode = 'MSGPINRESET';
          }
          if (data.data.features.messageMuting === undefined) {
            data.data.features.messageMuting = false;
          }
          if (data.data.features.eventTimeline === undefined) {
            data.data.features.eventTimeline = false;
          }
          if (data.data.features.diaryEditing === undefined) {
            data.data.features.diaryEditing = false;
          }
          // Ensure documentManagement autoShow fields exist with defaults
          if (data.data.features.documentManagement) {
            if (data.data.features.documentManagement.showStudyFilesOnSite === undefined) {
              data.data.features.documentManagement.showStudyFilesOnSite = false;
            }
            if (data.data.features.documentManagement.autoShowStudy === undefined) {
              data.data.features.documentManagement.autoShowStudy = false;
            }
            if (data.data.features.documentManagement.autoShowSite === undefined) {
              data.data.features.documentManagement.autoShowSite = false;
            }
            if (data.data.features.documentManagement.autoShowParticipant === undefined) {
              data.data.features.documentManagement.autoShowParticipant = false;
            }
          }
          // Ensure sensorUploads object exists with defaults when missing
          if (!data.data.features.sensorUploads) {
            data.data.features.sensorUploads = {
              enabled: false,
              readerType: 'dexcom',
            };
          } else if (!data.data.features.sensorUploads.readerType) {
            data.data.features.sensorUploads.readerType = 'dexcom';
          }
          if (data.data.features.siteCgmExport === undefined) {
            data.data.features.siteCgmExport = false;
          }
        }

        if (!data.data.permissions) {
          data.data.permissions = { actionRoles: {} };
        } else if (!data.data.permissions.actionRoles || typeof data.data.permissions.actionRoles !== 'object') {
          data.data.permissions.actionRoles = {};
        }

        // Ensure each property has a hidden field (default to false for existing properties)
        if (data.data.properties && Array.isArray(data.data.properties)) {
          data.data.properties = data.data.properties.map(prop => ({
            ...prop,
            hidden: prop.hidden ?? false
          }));
        }

        // If completedStatusCodes doesn't exist, initialize with default
        if (!data.data.completedStatusCodes || !Array.isArray(data.data.completedStatusCodes) || data.data.completedStatusCodes.length === 0) {
          data.data.completedStatusCodes = ['COMPD'];
        }
        if (!data.data.activeStatusCodes || !Array.isArray(data.data.activeStatusCodes) || data.data.activeStatusCodes.length === 0) {
          data.data.activeStatusCodes = ['ENRO'];
        }
        if (!data.data.excludedSiteIds || !Array.isArray(data.data.excludedSiteIds)) {
          data.data.excludedSiteIds = [];
        }

        propertiesData.set(data.data);
        // Initialize the completedStatusCodes input field
        initCompletedStatusCodesInput();
        initActiveStatusCodesInput();
        initExcludedSiteIdsInput();
        // Extract unique groups
        const groups = [...new Set(data.data.properties.map((p) => p.group).filter(Boolean))];
        propertyGroups.set(groups);
      }
    } catch (error) {
      console.error('Error fetching study properties:', error);
      error = 'Failed to load study properties';
    }
  }

  function addProperty() {
    propertiesData.update((data) => ({
      ...data,
      properties: [
        ...data.properties,
        {
          key: '',
          label: '',
          type: 'text',
          required: false,
          hidden: false,
          defaultValue: null,
          options: [],
          validation: {},
          description: '',
          order: data.properties.length,
          group: '',
          active: true,
        },
      ],
    }));
  }

  function removeProperty(index) {
    propertiesData.update((data) => ({
      ...data,
      properties: data.properties.filter((_, i) => i !== index),
    }));
  }

  function addOption(propertyIndex) {
    propertiesData.update((data) => ({
      ...data,
      properties: data.properties.map((prop, i) => {
        if (i === propertyIndex) {
          return {
            ...prop,
            options: [...prop.options, { code: '', label: '' }],
          };
        }
        return prop;
      }),
    }));
  }

  function removeOption(propertyIndex, optionIndex) {
    propertiesData.update((data) => ({
      ...data,
      properties: data.properties.map((prop, i) => {
        if (i === propertyIndex) {
          return {
            ...prop,
            options: prop.options.filter((_, j) => j !== optionIndex),
          };
        }
        return prop;
      }),
    }));
  }

  function addGroup() {
    const newGroup = prompt('Enter new group name:');
    if (newGroup) {
      propertyGroups.update((groups) => [...groups, newGroup]);
    }
  }

  function expandSection(section) {
    // This function is no longer needed for the tabbed interface
    // expandedSection = section;
  }

  async function handleSubmit() {
    try {
      // Ensure status codes and excludedSiteIds are synced from inputs before saving
      updateCompletedStatusCodes();
      updateActiveStatusCodes();
      updateExcludedSiteIds();
      
      const dataToSend = get(propertiesData);
      // Basic client-side validation for CGM valid range
      const vr = dataToSend?.glucoseParameters?.cgmValidRange;
      if (vr && (typeof vr.low === 'number') && (typeof vr.high === 'number')) {
        if (vr.low >= vr.high) {
          error = 'CGM Valid Range: Lower bound must be less than upper bound.';
          return;
        }
      }
      console.log('Sending study properties data:', JSON.stringify(dataToSend, null, 2));
      
      const response = await fetch(`${API_BASE_URL}/studyProperties/${$selectedStudy._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dataToSend),
      });

      if (!response.ok) {
        throw new Error('Failed to update study properties');
      }

      // Refresh the global studyProperties store so other components pick up the changes
      await refreshStudyProperties($selectedStudy._id);

      dispatch('submit');
      close();
    } catch (error) {
      console.error('Error updating study properties:', error);
      error = 'Failed to update study properties';
    }
  }

  function toggleActionsMenu() {
    showActionsMenu = !showActionsMenu;
  }

  async function handleExportStudyConfig() {
    if (!$selectedStudy || !$selectedStudy._id) {
      console.warn('No study selected');
      return;
    }

    try {
      // Fetch StudyProperties and StudyLanguages for the current study
      const [propertiesRes, languagesRes] = await Promise.all([
        fetch(`${API_BASE_URL}/studyProperties/${$selectedStudy._id}`),
        fetch(`${API_BASE_URL}/languages/study/${$selectedStudy._id}`)
      ]);

      if (!propertiesRes.ok) {
        throw new Error('Failed to fetch study properties');
      }

      const propertiesResponse = await propertiesRes.json();
      const languagesData = languagesRes.ok ? await languagesRes.json() : [];

      // Extract the data from the response (backend returns { data: {...} })
      const properties = propertiesResponse.data || propertiesResponse;

      console.log('Exporting study properties:', properties);

      // Create a clean export object
      const exportData = {
        exportDate: new Date().toISOString(),
        sourceStudyId: $selectedStudy._id,
        sourceStudyName: $selectedStudy.studyName || 'Unknown Study',
        sourceProtocolNumber: $selectedStudy.protocolNumber || '',
        studyProperties: properties || {},
        studyLanguages: Array.isArray(languagesData) ? languagesData.map(lang => ({
          languageGUID: lang.languageGUID,
          language: lang.language,
          code: lang.code,
          flag: lang.flag
        })) : []
      };

      // Create a blob and download it
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `study-config-${$selectedStudy.protocolNumber || 'study'}-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      showActionsMenu = false;
      alert('Study configuration exported successfully');
    } catch (error) {
      console.error('Error exporting study configuration:', error);
      alert('Failed to export study configuration. Please try again.');
    }
  }

  function handleImportClick() {
    // Trigger the hidden file input
    if (fileInputRef) {
      fileInputRef.click();
    }
    showActionsMenu = false;
  }

  async function handleFileSelected(event) {
    const file = event.target.files[0];
    if (!file) {
      return;
    }

    if (!$selectedStudy || !$selectedStudy._id) {
      alert('Please select a study first');
      return;
    }

    try {
      const fileContent = await file.text();
      const importData = JSON.parse(fileContent);

      if (!importData.studyProperties && !importData.studyLanguages) {
        throw new Error('Invalid import file format: no study configuration data found');
      }

      // Confirm import
      const confirmMessage = `This will import study configuration into "${$selectedStudy.studyName}".\n\nThis will update:\n- Study Properties\n- Study Languages\n\nContinue?`;
      if (!confirm(confirmMessage)) {
        event.target.value = '';
        return;
      }

      let results = {
        propertiesSuccess: false,
        languagesSuccess: 0,
        languagesFailed: 0
      };

      // Import StudyProperties
      if (importData.studyProperties) {
        try {
          // Prepare properties data, removing fields that shouldn't be copied
          // Note: studyGUID is passed in URL params, not in body
          const { _id, studyGUID, createDate, lastUpdateDate, createBy, createByGUID, lastUpdateBy, lastUpdateByGUID, __v, ...cleanProperties } = importData.studyProperties;
          const propertiesToImport = cleanProperties;

          console.log('Importing study properties for study:', $selectedStudy._id);
          console.log('Properties to import:', propertiesToImport);

          const propertiesRes = await fetch(`${API_BASE_URL}/studyProperties/${$selectedStudy._id}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(propertiesToImport),
          });

          console.log('Properties import response status:', propertiesRes.status);

          if (propertiesRes.ok) {
            results.propertiesSuccess = true;
            console.log('Study properties imported successfully');
          } else {
            const errorText = await propertiesRes.text();
            console.error('Failed to update study properties. Status:', propertiesRes.status, 'Response:', errorText);
          }
        } catch (error) {
          console.error('Error importing study properties:', error);
        }
      }

      // Import StudyLanguages
      if (importData.studyLanguages && Array.isArray(importData.studyLanguages)) {
        for (const lang of importData.studyLanguages) {
          try {
            // Check if language already exists for this study
            const existingLangs = await fetch(`${API_BASE_URL}/languages/study/${$selectedStudy._id}`);
            const existingLangsData = await existingLangs.json();
            const langExists = Array.isArray(existingLangsData) && existingLangsData.some(
              l => l.languageGUID === lang.languageGUID
            );

            if (!langExists) {
              // Add language to study
              const langRes = await fetch(`${API_BASE_URL}/languages/study/${$selectedStudy._id}/${lang.languageGUID}`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
              });

              if (langRes.ok) {
                results.languagesSuccess++;
              } else {
                results.languagesFailed++;
              }
            } else {
              results.languagesSuccess++;
            }
          } catch (error) {
            results.languagesFailed++;
            console.error(`Error importing language ${lang.language}:`, error);
          }
        }
      }

      // Reset file input
      event.target.value = '';

      // Show result
      const resultMessage = `Import complete!\n\nStudy Properties: ${results.propertiesSuccess ? 'Success' : 'Failed'}\nLanguages: ${results.languagesSuccess} succeeded${results.languagesFailed > 0 ? `, ${results.languagesFailed} failed` : ''}`;
      alert(resultMessage);

      // Refresh the data if anything succeeded
      if (results.propertiesSuccess || results.languagesSuccess > 0) {
        await fetchStudyProperties();
        await fetchStudyLanguages();
      }
    } catch (error) {
      console.error('Error importing study configuration:', error);
      alert(`Failed to import study configuration.\n\nError: ${error.message}\n\nPlease check the browser console for more details.`);
      // Reset file input
      event.target.value = '';
    }
  }

  function close() {
    // Reset completedStatusCodesInput
    completedStatusCodesInput = 'COMPD';
    activeStatusCodesInput = 'ENRO';
    excludedSiteIdsInput = '';
    // Reset properties data but don't change the selectedStudy
    propertiesData.set({
      properties: [],
      completedStatusCodes: ['COMPD'],
      activeStatusCodes: ['ENRO'],
      excludedSiteIds: [],
      schedulerEnabled: false,
      idMasks: {
        siteId: '',
        participantId: '{siteId}{###}',
        autoGenerateParticipantId: true,
        showGenerateParticipantIdButton: true,
      },
      features: {
        messaging: true,
        diary: true,
        diaryEditing: false,
        eventTimeline: false,
        smbg: true,
        workflows: false,
        performanceMetrics: true,
        documentManagement: {
          study: true,
          site: true,
          participant: true,
          showStudyFilesOnSite: false,
          autoShowStudy: false,
          autoShowSite: false,
          autoShowParticipant: false,
        },
        cardViews: {
          sites: true,
          participants: true,
          participant: true,
          cgmStatisticsReport: true,
          measurementsReport: true,
          agpReport: true,
        },
        schedule: true,
        compliance: true,
        auditLog: true,
        siteMessaging: true,
        siteCgmExport: false,
        randomization: {
          enabled: false,
          daysAfterEnrollment: 30,
        },
      },
      reports: {
        generalReports: {
          cgmDetails: true,
          cgmReadings: true,
          smbgDetails: true,
          diaryEntryDetails: true,
          insulinDailyTotals: true,
          alertsMessageDetails: true,
          eventReport: false,
          subjectCgm: true,
          sensorCgm: true,
          cgmCompletion: true,
        },
        participantReports: {
          cgmStatistics: true,
          measurements: true,
          agpDailyProfiles: true,
        },
      },
      permissions: {
        actionRoles: {},
      },
      glucoseParameters: {
        cgmCompliance: {
          complianceThreshold: 80,
          complianceRange: 30,
          complianceFocus: 7,
        },
        cgmValidRange: {
          low: 40,
          high: 400,
        },
        smbgCompliance: {
          complianceThreshold: 80,
          complianceRange: 30,
          complianceFocus: 7,
          targetReadingCount: 4,
        },
        thresholds: {
          veryLow: 54,
          low: 70,
          high: 180,
          veryHigh: 250,
        },
        events: {
          hypoThreshold: 54,
          hyperThreshold: 250,
          eventDuration: 3,
        },
      },
    });
    dispatch('close');
  }
</script>

{#if showForm}
  <div class="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div
      class="modal-overlay absolute inset-0 bg-slate-800 opacity-75"
      on:click={close}
      on:keydown={(e) => e.key === 'Escape' && close()}
      role="button"
      tabindex="0"
    ></div>
    <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
    <div
      class="bg-white dark:bg-gray-900 p-4 rounded-lg shadow-lg w-full max-w-7xl h-[90vh] relative z-60 flex flex-col"
      on:click|stopPropagation
      on:keydown|stopPropagation
      role="dialog"
    >
      <header class="flex justify-between items-center mb-4">
        <h2 class="text-xl font-bold text-gray-900 dark:text-gray-100">
          Study Settings
          {#if $selectedStudy}
            <span class="text-base font-normal text-gray-600 dark:text-gray-400 ml-2">
              - {$selectedStudy.studyName} ({$selectedStudy.protocolNumber})
            </span>
          {/if}
        </h2>
        <div class="flex items-center gap-2">
          {#if isAdmin}
            <div class="relative">
              <button 
                type="button"
                on:click={toggleActionsMenu}
                class="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 p-2"
                title="More actions (Admin only)"
              >
                <FontAwesomeIcon icon={faEllipsisVertical} />
              </button>
              {#if showActionsMenu}
                <div class="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-md shadow-lg z-50 border border-gray-200 dark:border-gray-700">
                  <div class="py-1">
                    <button
                      type="button"
                      on:click={handleExportStudyConfig}
                      class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3"
                    >
                      <FontAwesomeIcon icon={faFileExport} class="w-4" />
                      Export study configuration
                    </button>
                    <button
                      type="button"
                      on:click={handleImportClick}
                      class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3"
                    >
                      <FontAwesomeIcon icon={faFileImport} class="w-4" />
                      Import study configuration
                    </button>
                  </div>
                </div>
              {/if}
            </div>
          {/if}
          <button
            on:click={close}
            class="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
          >
            &times;
          </button>
        </div>
      </header>

      <form on:submit|preventDefault={handleSubmit} class="flex flex-col flex-1 min-h-0">

        <!-- Tab Navigation -->
        <div class="flex border-b border-gray-200 dark:border-gray-700 mb-4">
          {#each TABS as tab}
            <button
              type="button"
              on:click={() => activeTab = tab.id}
              class="flex-1 py-2 px-4 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              class:bg-blue-500={activeTab === tab.id}
              class:text-white={activeTab === tab.id}
              class:dark:bg-blue-600={activeTab === tab.id}
              class:dark:text-white={activeTab === tab.id}
            >
              <FontAwesomeIcon icon={tab.icon} class="mr-2" />
              {tab.label}
            </button>
          {/each}
        </div>

        <!-- Tab Content -->
        <div class="flex-1 overflow-y-auto" style="height: 0;">
          {#if activeTab === 'languages'}
            <div class="p-3">
              <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                Mobile App Languages
              </h3>
              <div class="text-xs text-gray-600 dark:text-gray-400 mb-3">
                Select the languages available for this study.
              </div>
              {#if isLoadingLanguages}
                <div class="text-center py-4 text-gray-500 dark:text-gray-400">
                  Loading languages...
                </div>
              {:else if languagesError}
                <div class="text-center py-4 text-red-500">{languagesError}</div>
              {:else}
                <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                  {#each languages as language}
                    <div
                      class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                    >
                      <input
                        type="checkbox"
                        id={`lang-${language._id}`}
                        checked={isLanguageAssigned(language._id)}
                        on:change={() =>
                          toggleLanguageAssignment(language._id, isLanguageAssigned(language._id))}
                        class="form-checkbox h-4 w-4 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                      />
                      <label for={`lang-${language._id}`} class="flex-1 cursor-pointer">
                        <div class="flex items-center space-x-1 text-xs">
                          <span class="text-base">{language.flag}</span>
                          <span class="font-medium text-gray-900 dark:text-gray-100"
                            >{language.language}</span
                          >
                          <span class="text-gray-500">({language.code})</span>
                        </div>
                      </label>
                    </div>
                  {/each}
                </div>
              {/if}
            </div>
          {:else if activeTab === 'idMasks'}
            <div class="p-3">
              <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                Patterns
              </h3>
              <div class="text-xs text-gray-600 dark:text-gray-400 mb-3">
                Site and subject ID patterns for new sites and participants.
              </div>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <!-- svelte-ignore a11y-label-has-associated-control -->
                  <label class="block text-xs font-medium text-gray-700 dark:text-gray-300">
                    Site ID Pattern
                  </label>
                  <input
                    type="text"
                    bind:value={$propertiesData.idMasks.siteId}
                    placeholder="Site ID pattern (optional)"
                    class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
                  />
                  <p class="text-xs text-gray-500 mt-1">
                    Example: ABC to limit site IDs to this prefix
                  </p>
                </div>
                <div>
                  <!-- svelte-ignore a11y-label-has-associated-control -->
                  <label class="block text-xs font-medium text-gray-700 dark:text-gray-300">
                    Participant ID Pattern
                  </label>
                  <div class="relative">
                    <input
                      type="text"
                      bind:value={$propertiesData.idMasks.participantId}
                      placeholder="SITE-001"
                      class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
                    />
                    <div class="absolute right-2 top-1" title="ID pattern format help">
                      <FontAwesomeIcon icon={faInfoCircle} class="text-gray-400" />
                    </div>
                  </div>
                  <p class="text-xs text-gray-500 mt-1">
                    Format: Use {'{'}siteId{'}'} for site ID and ### for sequential numbers
                  </p>
                </div>
              </div>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-2 mt-4">
                <label
                  class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                >
                  <input
                    type="checkbox"
                    bind:checked={$propertiesData.idMasks.autoGenerateParticipantId}
                    class="form-checkbox h-4 w-4 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                  />
                  <span class="text-sm text-gray-900 dark:text-gray-100">
                    Auto-generate subject IDs in new participant modal
                  </span>
                </label>
                <label
                  class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                >
                  <input
                    type="checkbox"
                    bind:checked={$propertiesData.idMasks.showGenerateParticipantIdButton}
                    class="form-checkbox h-4 w-4 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                  />
                  <span class="text-sm text-gray-900 dark:text-gray-100">
                    Show subject ID generate button in new participant modal
                  </span>
                </label>
              </div>
            </div>
          {:else if activeTab === 'features'}
            <div class="p-3">
              <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                Features
              </h3>
              <div class="text-xs text-gray-600 dark:text-gray-400 mb-3">
                Enable or disable features for this study.
              </div>

              <!-- Basic Features -->
              <div class="mb-4">
                <h4 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                  Basic Features
                </h4>
                <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                  <label
                    class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                  >
                    <input
                      type="checkbox"
                      bind:checked={$propertiesData.features.messaging}
                      class="form-checkbox h-4 w-4 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                    />
                    <span class="text-sm text-gray-900 dark:text-gray-100">Messaging</span>
                  </label>
                  <label
                    class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                  >
                    <input
                      type="checkbox"
                      bind:checked={$propertiesData.features.workflows}
                      class="form-checkbox h-4 w-4 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                    />
                    <span class="text-sm text-gray-900 dark:text-gray-100">Workflows</span>
                  </label>
                  <label
                    class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                  >
                    <input
                      type="checkbox"
                      bind:checked={$propertiesData.features.diary}
                      class="form-checkbox h-4 w-4 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                    />
                    <span class="text-sm text-gray-900 dark:text-gray-100">Diary</span>
                  </label>
                  <label
                    class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                  >
                    <input
                      type="checkbox"
                      bind:checked={$propertiesData.features.diaryEditing}
                      class="form-checkbox h-4 w-4 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                    />
                    <span class="text-sm text-gray-900 dark:text-gray-100">Diary Editing</span>
                  </label>
                  <label
                    class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                  >
                    <input
                      type="checkbox"
                      bind:checked={$propertiesData.features.eventTimeline}
                      class="form-checkbox h-4 w-4 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                    />
                    <span class="text-sm text-gray-900 dark:text-gray-100">Event Timeline</span>
                  </label>
                  <label
                    class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                  >
                    <input
                      type="checkbox"
                      bind:checked={$propertiesData.features.smbg}
                      class="form-checkbox h-4 w-4 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                    />
                    <span class="text-sm text-gray-900 dark:text-gray-100">SMBG</span>
                  </label>
                  <label
                    class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                  >
                    <input
                      type="checkbox"
                      bind:checked={$propertiesData.features.performanceMetrics}
                      class="form-checkbox h-4 w-4 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                    />
                    <span class="text-sm text-gray-900 dark:text-gray-100">Performance Metrics</span>
                  </label>
                  <label
                    class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                  >
                    <input
                      type="checkbox"
                      bind:checked={$propertiesData.features.schedule}
                      class="form-checkbox h-4 w-4 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                    />
                    <span class="text-sm text-gray-900 dark:text-gray-100">Schedule</span>
                  </label>
                  <label
                    class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                  >
                    <input
                      type="checkbox"
                      bind:checked={$propertiesData.features.compliance}
                      class="form-checkbox h-4 w-4 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                    />
                    <span class="text-sm text-gray-900 dark:text-gray-100">Compliance</span>
                  </label>
                  <label
                    class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                  >
                    <input
                      type="checkbox"
                      bind:checked={$propertiesData.features.auditLog}
                      class="form-checkbox h-4 w-4 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                    />
                    <span class="text-sm text-gray-900 dark:text-gray-100">Audit Log</span>
                  </label>
                  <label
                    class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                  >
                    <input
                      type="checkbox"
                      bind:checked={$propertiesData.features.visitTags}
                      class="form-checkbox h-4 w-4 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                    />
                    <span class="text-sm text-gray-900 dark:text-gray-100">Visit Tags</span>
                  </label>
                  <label
                    class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                  >
                    <input
                      type="checkbox"
                      bind:checked={$propertiesData.features.siteMessaging}
                      class="form-checkbox h-4 w-4 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                    />
                    <span class="text-sm text-gray-900 dark:text-gray-100">Site Messaging</span>
                  </label>
                  <label
                    class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                  >
                    <input
                      type="checkbox"
                      bind:checked={$propertiesData.features.pinReset}
                      class="form-checkbox h-4 w-4 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                    />
                    <span class="text-sm text-gray-900 dark:text-gray-100">PIN Reset</span>
                  </label>
                  <label
                    class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                  >
                    <input
                      type="checkbox"
                      bind:checked={$propertiesData.features.messageMuting}
                      class="form-checkbox h-4 w-4 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                    />
                    <span class="text-sm text-gray-900 dark:text-gray-100">Message Muting</span>
                  </label>
                  <label
                    class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                  >
                    <input
                      type="checkbox"
                      bind:checked={$propertiesData.features.upgradeEligibility}
                      class="form-checkbox h-4 w-4 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                    />
                    <span class="text-sm text-gray-900 dark:text-gray-100">Upgrade Eligibility</span>
                  </label>
                  <label
                    class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                  >
                    <input
                      type="checkbox"
                      bind:checked={$propertiesData.schedulerEnabled}
                      class="form-checkbox h-4 w-4 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                    />
                    <span class="text-sm text-gray-900 dark:text-gray-100">Job Scheduler</span>
                  </label>
                </div>
                {#if $propertiesData.features.pinReset}
                  <div class="mt-2 p-3 border border-gray-200 dark:border-gray-700 rounded bg-gray-50 dark:bg-gray-800">
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <!-- svelte-ignore a11y-label-has-associated-control -->
                        <label class="block text-xs font-medium text-gray-700 dark:text-gray-300">
                          PIN Reset Message Type
                        </label>
                        <select
                          bind:value={$propertiesData.features.pinResetMessageTypeCode}
                          class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
                        >
                          {#each messageTypeOptions as option}
                            <option value={option.code}>{option.label} ({option.code})</option>
                          {/each}
                        </select>
                        <p class="text-xs text-gray-500 mt-1">
                          Message type sent to the participant when a PIN reset is triggered
                        </p>
                      </div>
                    </div>
                  </div>
                {/if}
              </div>

              <!-- Randomization -->
              <div class="mb-4">
                <h4 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                  Randomization
                </h4>
                <div class="grid grid-cols-1 gap-4">
                  <label
                    class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                  >
                    <input
                      type="checkbox"
                      bind:checked={$propertiesData.features.randomization.enabled}
                      class="form-checkbox h-4 w-4 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                    />
                    <span class="text-sm text-gray-900 dark:text-gray-100">Enable Randomization</span>
                  </label>
                  {#if $propertiesData.features.randomization.enabled}
                    <div class="ml-6 p-3 border border-gray-200 dark:border-gray-700 rounded bg-gray-50 dark:bg-gray-800">
                      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <!-- svelte-ignore a11y-label-has-associated-control -->
                          <label class="block text-xs font-medium text-gray-700 dark:text-gray-300">
                            Days After Enrollment
                          </label>
                          <input
                            type="number"
                            min="0"
                            max="365"
                            bind:value={$propertiesData.features.randomization.daysAfterEnrollment}
                            class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
                          />
                          <p class="text-xs text-gray-500 mt-1">
                            Number of days after enrollment when randomization occurs
                          </p>
                        </div>
                      </div>
                    </div>
                  {/if}
                </div>
              </div>

              <!-- Document Management -->
              <div class="mb-4">
                <h4 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                  Document Management
                </h4>
                <div class="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <label
                    class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                  >
                    <input
                      type="checkbox"
                      bind:checked={$propertiesData.features.documentManagement.study}
                      class="form-checkbox h-4 w-4 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                    />
                    <span class="text-sm text-gray-900 dark:text-gray-100">Study Level</span>
                  </label>
                  <label
                    class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                  >
                    <input
                      type="checkbox"
                      bind:checked={$propertiesData.features.documentManagement.site}
                      class="form-checkbox h-4 w-4 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                    />
                    <span class="text-sm text-gray-900 dark:text-gray-100">Site Level</span>
                  </label>
                  <label
                    class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                  >
                    <input
                      type="checkbox"
                      bind:checked={$propertiesData.features.documentManagement.participant}
                      class="form-checkbox h-4 w-4 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                    />
                    <span class="text-sm text-gray-900 dark:text-gray-100">Participant Level</span>
                  </label>
                </div>
                <h4 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2 mt-3">
                  Site View Inheritance
                </h4>
                <div class="grid grid-cols-1 gap-2">
                  <label
                    class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                  >
                    <input
                      type="checkbox"
                      bind:checked={$propertiesData.features.documentManagement.showStudyFilesOnSite}
                      class="form-checkbox h-4 w-4 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                    />
                    <span class="text-sm text-gray-900 dark:text-gray-100">Show study-level files on site level</span>
                  </label>
                </div>
                <h4 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2 mt-3">
                  Auto-Show Documents
                </h4>
                <div class="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <label
                    class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                  >
                    <input
                      type="checkbox"
                      bind:checked={$propertiesData.features.documentManagement.autoShowStudy}
                      class="form-checkbox h-4 w-4 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                    />
                    <span class="text-sm text-gray-900 dark:text-gray-100">Study Level</span>
                  </label>
                  <label
                    class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                  >
                    <input
                      type="checkbox"
                      bind:checked={$propertiesData.features.documentManagement.autoShowSite}
                      class="form-checkbox h-4 w-4 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                    />
                    <span class="text-sm text-gray-900 dark:text-gray-100">Site Level</span>
                  </label>
                  <label
                    class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                  >
                    <input
                      type="checkbox"
                      bind:checked={$propertiesData.features.documentManagement.autoShowParticipant}
                      class="form-checkbox h-4 w-4 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                    />
                    <span class="text-sm text-gray-900 dark:text-gray-100">Participant Level</span>
                  </label>
                </div>
              </div>

              <!-- Card Views -->
              <div class="mb-4">
                <h4 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Card Views</h4>
                <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                  <label
                    class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                  >
                    <input
                      type="checkbox"
                      bind:checked={$propertiesData.features.cardViews.sites}
                      class="form-checkbox h-4 w-4 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                    />
                    <span class="text-sm text-gray-900 dark:text-gray-100">Sites</span>
                  </label>
                  <label
                    class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                  >
                    <input
                      type="checkbox"
                      bind:checked={$propertiesData.features.cardViews.participants}
                      class="form-checkbox h-4 w-4 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                    />
                    <span class="text-sm text-gray-900 dark:text-gray-100">Participants</span>
                  </label>
                  <label
                    class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                  >
                    <input
                      type="checkbox"
                      bind:checked={$propertiesData.features.cardViews.participant}
                      class="form-checkbox h-4 w-4 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                    />
                    <span class="text-sm text-gray-900 dark:text-gray-100">Participant</span>
                  </label>
                  <label
                    class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                  >
                    <input
                      type="checkbox"
                      bind:checked={$propertiesData.features.cardViews.cgmStatisticsReport}
                      class="form-checkbox h-4 w-4 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                    />
                    <span class="text-sm text-gray-900 dark:text-gray-100">CGM Statistics Report</span
                    >
                  </label>
                  <label
                    class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                  >
                    <input
                      type="checkbox"
                      bind:checked={$propertiesData.features.cardViews.measurementsReport}
                      class="form-checkbox h-4 w-4 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                    />
                    <span class="text-sm text-gray-900 dark:text-gray-100">Measurements Report</span>
                  </label>
                  <label
                    class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                  >
                    <input
                      type="checkbox"
                      bind:checked={$propertiesData.features.cardViews.agpReport}
                      class="form-checkbox h-4 w-4 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                    />
                    <span class="text-sm text-gray-900 dark:text-gray-100">AGP Report</span>
                  </label>
                </div>
              </div>

              <!-- Participant Statistics -->
              <div class="mb-4">
                <h4 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                  Participant Statistics
                </h4>
                <div class="grid grid-cols-1 gap-4">
                  <div>
                    <!-- svelte-ignore a11y-label-has-associated-control -->
                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300">
                      Active Status Codes
                    </label>
                    <input
                      type="text"
                      bind:value={activeStatusCodesInput}
                      on:blur={updateActiveStatusCodes}
                      on:keydown={(e) => e.key === 'Enter' && updateActiveStatusCodes()}
                      placeholder="ENRO, SCRE"
                      class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
                    />
                    <p class="text-xs text-gray-500 mt-1">
                      Comma-separated participant statuses considered active and collecting CGM. Individual scheduler jobs can override this list.
                    </p>
                  </div>
                  <div>
                    <!-- svelte-ignore a11y-label-has-associated-control -->
                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300">
                      Completed Status Codes
                    </label>
                    <input
                      type="text"
                      bind:value={completedStatusCodesInput}
                      on:blur={updateCompletedStatusCodes}
                      on:keydown={(e) => e.key === 'Enter' && updateCompletedStatusCodes()}
                      placeholder="COMPD, COMP"
                      class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
                    />
                    <p class="text-xs text-gray-500 mt-1">
                      Comma-separated status codes to count as "End of Participation" (e.g., COMPD, DISC, SCFA)
                    </p>
                  </div>
                  <div>
                    <!-- svelte-ignore a11y-label-has-associated-control -->
                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300">
                      Excluded Site IDs
                    </label>
                    <input
                      type="text"
                      bind:value={excludedSiteIdsInput}
                      on:blur={updateExcludedSiteIds}
                      on:keydown={(e) => e.key === 'Enter' && updateExcludedSiteIds()}
                      placeholder="999, 998, 997"
                      class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
                    />
                    <p class="text-xs text-gray-500 mt-1">
                      Comma-separated site IDs to exclude from site lists and reports.
                    </p>
                  </div>
                </div>
              </div>

              <!-- Sensor Uploads -->
              <div class="mb-4">
                <h4 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                  Sensor Uploads
                </h4>
                <div class="grid grid-cols-1 gap-4">
                  <label
                    class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                  >
                    <input
                      type="checkbox"
                      bind:checked={$propertiesData.features.sensorUploads.enabled}
                      class="form-checkbox h-4 w-4 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                    />
                    <span class="text-sm text-gray-900 dark:text-gray-100">Load Sensor Data</span>
                  </label>
                  {#if $propertiesData.features.sensorUploads.enabled}
                    <div class="ml-6 p-3 border border-gray-200 dark:border-gray-700 rounded bg-gray-50 dark:bg-gray-800">
                      <div class="text-xs text-gray-600 dark:text-gray-400 mb-2">
                        Reader Type
                      </div>
                      <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <label
                          class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                        >
                          <input
                            type="radio"
                            name="sensor-upload-reader"
                            value="dexcom"
                            bind:group={$propertiesData.features.sensorUploads.readerType}
                            class="form-radio h-4 w-4 text-sky-800 dark:text-red-500 border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                          />
                          <span class="text-sm text-gray-900 dark:text-gray-100">Dexcom Reader</span>
                        </label>
                        <label
                          class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                        >
                          <input
                            type="radio"
                            name="sensor-upload-reader"
                            value="abbott"
                            bind:group={$propertiesData.features.sensorUploads.readerType}
                            class="form-radio h-4 w-4 text-sky-800 dark:text-red-500 border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                          />
                          <span class="text-sm text-gray-900 dark:text-gray-100">Abbott Reader</span>
                        </label>
                      </div>
                    </div>
                  {/if}
                </div>
              </div>

              <div class="mb-4">
                <h4 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                  Site CGM Export
                </h4>
                <div class="grid grid-cols-1 gap-4">
                  <label
                    class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                  >
                    <input
                      type="checkbox"
                      bind:checked={$propertiesData.features.siteCgmExport}
                      class="form-checkbox h-4 w-4 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                    />
                    <span class="text-sm text-gray-900 dark:text-gray-100">Enable site CGM export</span>
                  </label>
                </div>
              </div>
            </div>
          {:else if activeTab === 'access'}
            <div class="p-3 w-full min-w-0">
              <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                Access
              </h3>

              <div class="w-full space-y-6">
                {#each ACCESS_UI_GROUPS as group}
                  {@const groupActions = actionsForAccessGroup(group.id)}
                  {#if groupActions.length}
                    <section
                      class="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-stone-50/60 dark:bg-gray-800/40 p-4"
                    >
                      <h4 class="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">
                        {group.title}
                      </h4>
                      <p class="text-xs text-gray-600 dark:text-gray-400 mb-3">
                        {group.description}
                      </p>
                      <div class="w-full divide-y divide-gray-200 dark:divide-gray-600 border-t border-gray-200 dark:border-gray-600">
                        {#each groupActions as action}
                          <div class="grid grid-cols-1 xl:grid-cols-12 gap-3 py-3 w-full">
                            <div class="xl:col-span-5 min-w-0">
                              <div class="text-sm font-medium text-gray-800 dark:text-gray-200">
                                {action.label}
                              </div>
                              <div class="text-[11px] font-mono text-gray-500 dark:text-gray-400 mt-0.5 break-all">
                                {action.key}
                              </div>
                              {#if action.description}
                                <p class="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                  {action.description}
                                </p>
                              {/if}
                              {#if action.requiresWrite}
                                <p class="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                  Requires write access
                                </p>
                              {/if}
                            </div>
                            <div
                              class="xl:col-span-7 flex flex-wrap items-center gap-x-4 gap-y-2 w-full min-w-0"
                            >
                              {#each ACCESS_ROLE_OPTIONS as role}
                                <label
                                  class="inline-flex items-center gap-1.5 text-xs text-gray-800 dark:text-gray-200 whitespace-nowrap"
                                >
                                  <input
                                    type="checkbox"
                                    checked={accessRoleChecked(action.key, role, $propertiesData.permissions)}
                                    on:change={(e) =>
                                      toggleAccessActionRole(action.key, role, e.currentTarget.checked)}
                                    class="form-checkbox h-3.5 w-3.5 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                                  />
                                  <span>{role}</span>
                                </label>
                              {/each}
                            </div>
                          </div>
                        {/each}
                      </div>
                    </section>
                  {/if}
                {/each}
              </div>
            </div>
          {:else if activeTab === 'reports'}
            <div class="p-3">
              <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                Reports
              </h3>
              <div class="text-xs text-gray-600 dark:text-gray-400 mb-3">
                Configure which reports are available in the sidebar menu.
              </div>

              <!-- General Reports -->
              <div class="mb-4">
                <h4 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                  General Reports
                </h4>
                <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                  <label
                    class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                  >
                    <input
                      type="checkbox"
                      bind:checked={$propertiesData.reports.generalReports.cgmDetails}
                      class="form-checkbox h-4 w-4 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                    />
                    <span class="text-sm text-gray-900 dark:text-gray-100">CGM Details</span>
                  </label>
                  <label
                    class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                  >
                    <input
                      type="checkbox"
                      bind:checked={$propertiesData.reports.generalReports.cgmReadings}
                      class="form-checkbox h-4 w-4 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                    />
                    <span class="text-sm text-gray-900 dark:text-gray-100">CGM Readings</span>
                  </label>
                  <label
                    class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                  >
                    <input
                      type="checkbox"
                      bind:checked={$propertiesData.reports.generalReports.smbgDetails}
                      class="form-checkbox h-4 w-4 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                    />
                    <span class="text-sm text-gray-900 dark:text-gray-100">SMBG Details</span>
                  </label>
                  <label
                    class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                  >
                    <input
                      type="checkbox"
                      bind:checked={$propertiesData.reports.generalReports.diaryEntryDetails}
                      class="form-checkbox h-4 w-4 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                    />
                    <span class="text-sm text-gray-900 dark:text-gray-100">Diary Entry Details</span>
                  </label>
                  <label
                    class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                  >
                    <input
                      type="checkbox"
                      bind:checked={$propertiesData.reports.generalReports.insulinDailyTotals}
                      class="form-checkbox h-4 w-4 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                    />
                    <span class="text-sm text-gray-900 dark:text-gray-100">Insulin Daily Totals</span>
                  </label>
                  <label
                    class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                  >
                    <input
                      type="checkbox"
                      bind:checked={$propertiesData.reports.generalReports.alertsMessageDetails}
                      class="form-checkbox h-4 w-4 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                    />
                    <span class="text-sm text-gray-900 dark:text-gray-100"
                      >Alerts/Message Details</span
                    >
                  </label>
                  <label
                    class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                  >
                    <input
                      type="checkbox"
                      bind:checked={$propertiesData.reports.generalReports.eventReport}
                      class="form-checkbox h-4 w-4 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                    />
                    <span class="text-sm text-gray-900 dark:text-gray-100">Event Report</span>
                  </label>
                  <label
                    class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                  >
                    <input
                      type="checkbox"
                      bind:checked={$propertiesData.reports.generalReports.subjectCgm}
                      class="form-checkbox h-4 w-4 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                    />
                    <span class="text-sm text-gray-900 dark:text-gray-100">Subject CGM</span>
                  </label>
                  <label
                    class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                  >
                    <input
                      type="checkbox"
                      bind:checked={$propertiesData.reports.generalReports.sensorCgm}
                      class="form-checkbox h-4 w-4 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                    />
                    <span class="text-sm text-gray-900 dark:text-gray-100">Sensor CGM</span>
                  </label>
                  <label
                    class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                  >
                    <input
                      type="checkbox"
                      bind:checked={$propertiesData.reports.generalReports.cgmCompletion}
                      class="form-checkbox h-4 w-4 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                    />
                    <span class="text-sm text-gray-900 dark:text-gray-100">CGM Completion</span>
                  </label>
                </div>
              </div>

              <!-- Participant Reports -->
              <div class="mb-4">
                <h4 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                  Participant Reports
                </h4>
                <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                  <label
                    class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                  >
                    <input
                      type="checkbox"
                      bind:checked={$propertiesData.reports.participantReports.cgmStatistics}
                      class="form-checkbox h-4 w-4 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                    />
                    <span class="text-sm text-gray-900 dark:text-gray-100">CGM Statistics</span>
                  </label>
                  <label
                    class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                  >
                    <input
                      type="checkbox"
                      bind:checked={$propertiesData.reports.participantReports.measurements}
                      class="form-checkbox h-4 w-4 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                    />
                    <span class="text-sm text-gray-900 dark:text-gray-100">Measurements</span>
                  </label>
                  <label
                    class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                  >
                    <input
                      type="checkbox"
                      bind:checked={$propertiesData.reports.participantReports.agpDailyProfiles}
                      class="form-checkbox h-4 w-4 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                    />
                    <span class="text-sm text-gray-900 dark:text-gray-100">AGP & Daily Profiles</span>
                  </label>
                </div>
              </div>

              <!-- Custom Reports -->
              <div class="mb-4">
                <h4 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                  Custom Reports
                </h4>
                <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                  <label
                    class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                  >
                    <input
                      type="checkbox"
                      bind:checked={$propertiesData.reports.customReports.periodCompliance}
                      class="form-checkbox h-4 w-4 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                    />
                    <span class="text-sm text-gray-900 dark:text-gray-100">Period Compliance</span>
                  </label>
                  <label
                    class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                  >
                    <input
                      type="checkbox"
                      bind:checked={$propertiesData.reports.customReports.periodStatistics}
                      class="form-checkbox h-4 w-4 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                    />
                    <span class="text-sm text-gray-900 dark:text-gray-100">Period Statistics</span>
                  </label>
                  <label
                    class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                  >
                    <input
                      type="checkbox"
                      bind:checked={$propertiesData.reports.customReports.cgmSensorStatistics}
                      class="form-checkbox h-4 w-4 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                    />
                    <span class="text-sm text-gray-900 dark:text-gray-100">CGM Sensor Statistics</span>
                  </label>
                  <label
                    class="flex items-center space-x-2 p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                  >
                    <input
                      type="checkbox"
                      bind:checked={$propertiesData.reports.customReports.cgmParticipantStatistics}
                      class="form-checkbox h-4 w-4 text-sky-800 dark:text-red-500 rounded border-gray-300 dark:border-gray-600 focus:ring-red-700 dark:focus:ring-red-500"
                    />
                    <span class="text-sm text-gray-900 dark:text-gray-100">CGM Participant Statistics</span>
                  </label>
                </div>
              </div>

              <!-- Report Settings -->
              {#if $propertiesData.reports?.reportSettings}
                <div class="mb-4">
                  <h4 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                    Report Settings
                  </h4>
                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label for="diary-entry-date-priority" class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Diary Entry Date Priority
                      </label>
                      <select
                        id="diary-entry-date-priority"
                        bind:value={$propertiesData.reports.reportSettings.diaryEntryDatePriority}
                        class="form-select h-8 w-full border px-2 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white"
                      >
                        <option value="formDate">Document Form Date (Default)</option>
                        <option value="properties">Properties Date First</option>
                      </select>
                      <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Controls which date source is used first when displaying the Form Date column.
                      </p>
                    </div>
                  </div>
                </div>
              {/if}
            </div>
          {:else if activeTab === 'glucose'}
            <div class="p-3">
              <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                Glucose Parameters
              </h3>
              <div class="text-xs text-gray-600 dark:text-gray-400 mb-3">
                Configure compliance settings and glucose thresholds for this study.
              </div>

              <!-- CGM Compliance Settings -->
              <div class="mb-4">
                <h4 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                  CGM Compliance Settings
                </h4>
                <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <!-- svelte-ignore a11y-label-has-associated-control -->
                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300">
                      CGM Interval (minutes)
                    </label>
                    <select
                      bind:value={$propertiesData.glucoseParameters.cgmIntervalMinutes}
                      class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
                    >
                      <option value={1}>1</option>
                      <option value={5}>5</option>
                      <option value={15}>15</option>
                    </select>
                    <p class="text-xs text-gray-500 mt-1">Sampling interval for expected counts</p>
                  </div>
                  <div>
                    <!-- svelte-ignore a11y-label-has-associated-control -->
                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300">
                      Compliance Threshold (%)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      bind:value={$propertiesData.glucoseParameters.cgmCompliance.complianceThreshold}
                      class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
                    />
                    <p class="text-xs text-gray-500 mt-1">Percentage needed for compliance</p>
                  </div>
                  <div>
                    <!-- svelte-ignore a11y-label-has-associated-control -->
                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300">
                      Compliance Range (days)
                    </label>
                    <input
                      type="number"
                      min="1"
                      bind:value={$propertiesData.glucoseParameters.cgmCompliance.complianceRange}
                      class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
                    />
                    <p class="text-xs text-gray-500 mt-1">Days to show in chart</p>
                  </div>
                  <div>
                    <!-- svelte-ignore a11y-label-has-associated-control -->
                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300">
                      Compliance Focus (days)
                    </label>
                    <input
                      type="number"
                      min="1"
                      bind:value={$propertiesData.glucoseParameters.cgmCompliance.complianceFocus}
                      class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
                    />
                    <p class="text-xs text-gray-500 mt-1">Days to highlight</p>
                  </div>
                </div>
              </div>

              <!-- General CGM Device Duration -->
              <div class="mb-4">
                <h4 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                  CGM Device Duration (days)
                </h4>
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <!-- svelte-ignore a11y-label-has-associated-control -->
                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300">
                      Device Lifetime (days)
                    </label>
                    <input
                      type="number"
                      min="1"
                      step="0.5"
                      bind:value={$propertiesData.glucoseParameters.cgmDeviceDurationDays}
                      class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
                    />
                    <p class="text-xs text-gray-500 mt-1">Used to compute wear % and sensor progress</p>
                  </div>
                </div>
              </div>

              <!-- CGM Valid Range (Device Validity) -->
              <div class="mb-4">
                <h4 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                  CGM Valid Range (mg/dL)
                </h4>
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <!-- svelte-ignore a11y-label-has-associated-control -->
                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300">
                      Lower Bound
                    </label>
                    <input
                      type="number"
                      min="0"
                      bind:value={$propertiesData.glucoseParameters.cgmValidRange.low}
                      class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
                    />
                  </div>
                  <div>
                    <!-- svelte-ignore a11y-label-has-associated-control -->
                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300">
                      Upper Bound
                    </label>
                    <input
                      type="number"
                      min="0"
                      bind:value={$propertiesData.glucoseParameters.cgmValidRange.high}
                      class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
                    />
                  </div>
                </div>
                <p class="text-xs text-gray-500 mt-1">
                  Bounds used to treat CGM readings as valid for in-range filtering (defaults 40–400).
                </p>
              </div>

              <!-- SMBG Compliance Settings -->
              <div class="mb-4">
                <h4 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                  SMBG Compliance Settings
                </h4>
                <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <!-- svelte-ignore a11y-label-has-associated-control -->
                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300">
                      Compliance Threshold (%)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      bind:value={$propertiesData.glucoseParameters.smbgCompliance
                        .complianceThreshold}
                      class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
                    />
                    <p class="text-xs text-gray-500 mt-1">Percentage needed for compliance</p>
                  </div>
                  <div>
                    <!-- svelte-ignore a11y-label-has-associated-control -->
                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300">
                      Compliance Range (days)
                    </label>
                    <input
                      type="number"
                      min="1"
                      bind:value={$propertiesData.glucoseParameters.smbgCompliance.complianceRange}
                      class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
                    />
                    <p class="text-xs text-gray-500 mt-1">Days to show in chart</p>
                  </div>
                  <div>
                    <!-- svelte-ignore a11y-label-has-associated-control -->
                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300">
                      Compliance Focus (days)
                    </label>
                    <input
                      type="number"
                      min="1"
                      bind:value={$propertiesData.glucoseParameters.smbgCompliance.complianceFocus}
                      class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
                    />
                    <p class="text-xs text-gray-500 mt-1">Days to highlight</p>
                  </div>
                  <div>
                    <!-- svelte-ignore a11y-label-has-associated-control -->
                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300">
                      Target Reading Count
                    </label>
                    <input
                      type="number"
                      min="1"
                      bind:value={$propertiesData.glucoseParameters.smbgCompliance.targetReadingCount}
                      class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
                    />
                    <p class="text-xs text-gray-500 mt-1">
                      Expected SMBG readings per day (default: 4)
                    </p>
                  </div>
                </div>
              </div>

              <!-- Glucose Thresholds -->
              <div class="mb-4">
                <h4 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                  Glucose Thresholds (mg/dL)
                </h4>
                <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <!-- svelte-ignore a11y-label-has-associated-control -->
                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300">
                      Very Low
                    </label>
                    <input
                      type="number"
                      min="0"
                      bind:value={$propertiesData.glucoseParameters.thresholds.veryLow}
                      class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
                    />
                  </div>
                  <div>
                    <!-- svelte-ignore a11y-label-has-associated-control -->
                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300">
                      Low
                    </label>
                    <input
                      type="number"
                      min="0"
                      bind:value={$propertiesData.glucoseParameters.thresholds.low}
                      class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
                    />
                  </div>
                  <div>
                    <!-- svelte-ignore a11y-label-has-associated-control -->
                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300">
                      High
                    </label>
                    <input
                      type="number"
                      min="0"
                      bind:value={$propertiesData.glucoseParameters.thresholds.high}
                      class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
                    />
                  </div>
                  <div>
                    <!-- svelte-ignore a11y-label-has-associated-control -->
                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300">
                      Very High
                    </label>
                    <input
                      type="number"
                      min="0"
                      bind:value={$propertiesData.glucoseParameters.thresholds.veryHigh}
                      class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
                    />
                  </div>
                </div>
              </div>

              <!-- Event Configuration -->
              <div class="mb-4">
                <h4 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                  Event Configuration
                </h4>
                <div class="text-xs text-gray-600 dark:text-gray-400 mb-3">
                  Configure thresholds and duration for hypo and hyper events in CGM statistics.
                </div>
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <!-- svelte-ignore a11y-label-has-associated-control -->
                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300">
                      CGM Hypo Event Threshold (mg/dL)
                    </label>
                    <input
                      type="number"
                      min="0"
                      bind:value={$propertiesData.glucoseParameters.events.hypoThreshold}
                      class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
                    />
                    <p class="text-xs text-gray-500 mt-1">
                      Glucose level below which hypo events are counted
                    </p>
                  </div>
                  <div>
                    <!-- svelte-ignore a11y-label-has-associated-control -->
                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300">
                      CGM Hyper Event Threshold (mg/dL)
                    </label>
                    <input
                      type="number"
                      min="0"
                      bind:value={$propertiesData.glucoseParameters.events.hyperThreshold}
                      class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
                    />
                    <p class="text-xs text-gray-500 mt-1">
                      Glucose level above which hyper events are counted
                    </p>
                  </div>
                  <div>
                    <!-- svelte-ignore a11y-label-has-associated-control -->
                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300">
                      Event Duration (readings)
                    </label>
                    <input
                      type="number"
                      min="1"
                      bind:value={$propertiesData.glucoseParameters.events.eventDuration}
                      class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
                    />
                    <p class="text-xs text-gray-500 mt-1">
                      Number of consecutive readings to count as an event (typically 3 for 15 minutes)
                    </p>
                  </div>
                </div>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <!-- svelte-ignore a11y-label-has-associated-control -->
                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300">
                      SMBG Hypo Threshold (mg/dL)
                    </label>
                    <input
                      type="number"
                      min="0"
                      bind:value={$propertiesData.glucoseParameters.events.smbgHypoThreshold}
                      class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
                    />
                    <p class="text-xs text-gray-500 mt-1">
                      SMBG readings below this value are flagged as hypoglycemia
                    </p>
                  </div>
                  <div>
                    <!-- svelte-ignore a11y-label-has-associated-control -->
                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300">
                      SMBG Hyper Threshold (mg/dL)
                    </label>
                    <input
                      type="number"
                      min="0"
                      bind:value={$propertiesData.glucoseParameters.events.smbgHyperThreshold}
                      class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
                    />
                    <p class="text-xs text-gray-500 mt-1">
                      SMBG readings above this value are flagged as hyperglycemia
                    </p>
                  </div>
                </div>
              </div>
            </div>
          {:else if activeTab === 'properties'}
            <div class="p-3">
              <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                Subject Properties
              </h3>
              <div class="text-xs text-gray-600 dark:text-gray-400 mb-3">
                Define the properties that will be collected for each subject.
              </div>
              <div class="flex items-center gap-2 mb-3">
                <button
                  type="button"
                  on:click={addProperty}
                  class="pagination mx-1 p-0"
                  title="Add Property"
                >
                  <FontAwesomeIcon icon={faSquarePlus} class="text-sky-800 dark:text-white/70" />
                </button>
                <button
                  type="button"
                  on:click={addGroup}
                  class="pagination mx-1 p-0"
                  title="Add Group"
                >
                  <FontAwesomeIcon icon={faFolderPlus} class="text-sky-800 dark:text-white/70" />
                </button>
              </div>
              {#if $propertiesData.properties && $propertiesData.properties.length > 0}
                {#each $propertiesData.properties as property, index}
                  <div class="p-2 hover:bg-gray-50 dark:hover:bg-gray-800 rounded">
                    <div class="flex justify-between items-center mb-2">
                      <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        Property {index + 1}
                      </h3>
                      <button
                        type="button"
                        on:click={() => removeProperty(index)}
                        class="pagination mx-1 p-0"
                        title="Remove property"
                      >
                        <FontAwesomeIcon icon={faTrashCan} class="text-sky-800 dark:text-white/70" />
                      </button>
                    </div>

                    <div class="grid grid-cols-5 gap-2 mb-2">
                      <div>
                        <!-- svelte-ignore a11y-label-has-associated-control -->
                        <label class="block text-xs font-medium text-gray-700 dark:text-gray-300">
                          Key *
                        </label>
                        <input
                          type="text"
                          bind:value={property.key}
                          class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
                          required
                        />
                      </div>

                      <div>
                        <!-- svelte-ignore a11y-label-has-associated-control -->
                        <label class="block text-xs font-medium text-gray-700 dark:text-gray-300">
                          Label *
                        </label>
                        <input
                          type="text"
                          bind:value={property.label}
                          class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
                          required
                        />
                      </div>

                      <div>
                        <!-- svelte-ignore a11y-label-has-associated-control -->
                        <label class="block text-xs font-medium text-gray-700 dark:text-gray-300">
                          Type *
                        </label>
                        <select
                          bind:value={property.type}
                          class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
                          required
                        >
                          {#each PROPERTY_TYPES as type}
                            <option value={type.value}>{type.label}</option>
                          {/each}
                        </select>
                      </div>

                      <div>
                        <!-- svelte-ignore a11y-label-has-associated-control -->
                        <label class="block text-xs font-medium text-gray-700 dark:text-gray-300">
                          Required
                        </label>
                        <select
                          bind:value={property.required}
                          class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
                        >
                          <option value={false}>No</option>
                          <option value={true}>Yes</option>
                        </select>
                      </div>

                      <div>
                        <!-- svelte-ignore a11y-label-has-associated-control -->
                        <label class="block text-xs font-medium text-gray-700 dark:text-gray-300">
                          Hidden
                        </label>
                        <select
                          bind:value={property.hidden}
                          class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
                        >
                          <option value={false}>No</option>
                          <option value={true}>Yes</option>
                        </select>
                      </div>
                    </div>

                    <div class="grid grid-cols-2 gap-2">
                      <div>
                        <!-- svelte-ignore a11y-label-has-associated-control -->
                        <label class="block text-xs font-medium text-gray-700 dark:text-gray-300">
                          Group
                        </label>
                        <select
                          bind:value={property.group}
                          class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
                        >
                          <option value="">None</option>
                          {#each $propertyGroups as group}
                            <option value={group}>{group}</option>
                          {/each}
                        </select>
                      </div>

                      <div>
                        <!-- svelte-ignore a11y-label-has-associated-control -->
                        <label class="block text-xs font-medium text-gray-700 dark:text-gray-300">
                          Default Value
                        </label>
                        {#if property.type === 'boolean'}
                          <select
                            bind:value={property.defaultValue}
                            class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
                          >
                            <option value={null}>None</option>
                            <option value={true}>True</option>
                            <option value={false}>False</option>
                          </select>
                        {:else if property.type === 'select'}
                          <select
                            bind:value={property.defaultValue}
                            class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
                          >
                            <option value={null}>None</option>
                            {#each property.options as option}
                              <option value={option.code}>{option.label}</option>
                            {/each}
                          </select>
                        {:else if property.type === 'number'}
                          <input
                            type="number"
                            bind:value={property.defaultValue}
                            class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
                          />
                        {:else}
                          <input
                            type="text"
                            bind:value={property.defaultValue}
                            class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
                          />
                        {/if}
                      </div>
                    </div>

                    {#if property.type === 'select'}
                      <div class="mt-2">
                        <div class="flex justify-between items-center">
                          <!-- svelte-ignore a11y-label-has-associated-control -->
                          <label class="block text-xs font-medium text-gray-700 dark:text-gray-300">
                            Options
                          </label>
                          <button
                            type="button"
                            on:click={() => addOption(index)}
                            class="pagination mx-1 p-0"
                            title="Add option"
                          >
                            <FontAwesomeIcon
                              icon={faSquarePlus}
                              class="text-sky-800 dark:text-white/70"
                            />
                          </button>
                        </div>
                        {#each property.options as option, optionIndex}
                          <div class="flex gap-2 mt-1">
                            <input
                              type="text"
                              bind:value={option.code}
                              placeholder="Code"
                              class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
                            />
                            <input
                              type="text"
                              bind:value={option.label}
                              placeholder="Label"
                              class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
                            />
                            <button
                              type="button"
                              on:click={() => removeOption(index, optionIndex)}
                              class="pagination mx-1 p-0"
                              title="Remove option"
                            >
                              <FontAwesomeIcon
                                icon={faTrashCan}
                                class="text-sky-800 dark:text-white/70"
                              />
                            </button>
                          </div>
                        {/each}
                      </div>
                    {/if}
                  </div>
                {/each}
              {:else}
                <div class="flex items-center justify-center h-full">
                  <p class="text-gray-500 dark:text-gray-400 text-center p-6">
                    No properties defined for this study yet.<br />
                    Click the "+" button above to add a new property.
                  </p>
                </div>
              {/if}
            </div>
          {/if}
        </div>

        {#if error}
          <p class="text-red-500 text-sm mt-2">{error}</p>
        {/if}

        <div
          class="flex justify-end space-x-2 pt-4 mt-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0"
        >
          <button
            type="button"
            on:click={close}
            class="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
          >
            Cancel
          </button>
          <button type="submit" class="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600">
            Save
          </button>
        </div>
      </form>
    </div>
  </div>
{/if}

<!-- Hidden file input for import -->
<input
  type="file"
  accept=".json"
  bind:this={fileInputRef}
  on:change={handleFileSelected}
  class="hidden"
/>

<!-- Click outside handler for menu -->
{#if showActionsMenu}
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <!-- svelte-ignore a11y-no-static-element-interactions -->
  <div 
    class="fixed inset-0 z-40" 
    on:click={() => showActionsMenu = false}
  />
{/if}
